import { SELECT_ALL } from "blu-utilities/lib/models/constants";
import { CatalogItem } from "src/app/core/models/catalogs";
import { AsyncReport } from "../reports/async/models/async-report";
import { ReportIds, ReportInfo, ReportInfoMap } from "./constants";

export function getReportInfoFromId(reportId: ReportIds): ReportInfo | null {
  return ReportInfoMap.get(reportId) ?? null;
}

export function getReportInfoFromCode(reportCode: string): ReportInfo | null {
  let result = null;
  ReportInfoMap.forEach((report: ReportInfo) => {
    if (report.code === reportCode && report.isVisible) {
      result = report;
    }
  });
  return result;
}

/**
 * Function to get partner IDs based on the provided `partnerIds` and `partners` data.
 * If `SELECT_ALL` is present in `partnerIds`, it returns the IDs of all partners.
 *
 * @param partnerIds - Array of partner IDs (either number or string).
 * @param partners - Array of partner objects (must have an `id` property of type number or string).
 * @returns Array of IDs, either number or string.
 */
export function getPartnerIds(
  partnerIds: (number | string)[],
  partners: CatalogItem[],
): number[] {
  if (!partnerIds || partnerIds.length === 0) {
    return [];
  }
  let result: number[] = [];
  if (partnerIds?.includes(SELECT_ALL)) {
    result = partners.map((partner) => partner.id); // Get all partner IDs as numbers
  } else {
    // Otherwise, filter `partnerIds` and only include the ones that are numbers
    result = partnerIds.filter((id): id is number => typeof id === "number");
  }

  return result; // Return the array of numbers
}

export type SelectAllOption = Record<string, string>;

/**
 * Prepends a synthetic "select all" item to an ng-select `[items]` list,
 * replacing the old `@if (list.length > 0) { <ng-option value="all"> }` markup.
 * The item is only added when `items` is not empty.
 *
 * @param items - The real option items.
 * @param label - Text shown for the "select all" item (e.g. "All Partners").
 * @param labelKey - The ng-select `bindLabel` of the list (default "name").
 * @param valueKey - The ng-select `bindValue` of the list (default "id").
 * @param value - The value the control receives (default `SELECT_ALL`).
 */
export function withSelectAllOption<Item>(
  items: Item[],
  label: string,
  labelKey = "name",
  valueKey = "id",
  value: string = SELECT_ALL,
): (Item | SelectAllOption)[] {
  if (items.length === 0) {
    return items;
  }
  return [{ [labelKey]: label, [valueKey]: value }, ...items];
}

export interface AdminPendingReports {
  adminId: number | number;
  reports: AsyncReport[];
}
